import React, { FormEvent, useCallback, useState } from 'react'

import { Text, Box, Input, Button, Icon } from '@vividseats/vivid-ui-kit'
import InputMask, { InputState } from 'react-input-mask'

import apiFactory, { errorInterceptor } from '@/api/factory'
import appLogger from '@/utils/app-logger'

import { REFERRAL_DOWNLOAD_URL_STRING, ERROR_MESSAGE, BUTTON_LABELS, API_SUCCESS_RESPONSE } from './constants'
import styles from './styles.module.scss'
type RequestType = {
    phoneNumber: string
    isReferral?: boolean
    referralDownloadUrlString?: string
}

// Must point to a relative URL (e.g. the relative instance of `vivid-web`), not EXTERNAL_LEGACY_WEB_URL.
// See https://vividseats.atlassian.net/browse/ARCH-8195 (HAProxy Deprecation)
const legacyWebService = apiFactory('')
legacyWebService.interceptors.response.use((res) => res, errorInterceptor)

const SendDownloadLink: React.FC = () => {
    const [phoneNumber, setPhoneNumber] = useState<string>('')
    const [errorMsg, setErrorMsg] = useState<string | null>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [buttonLabel, setButtonLabel] = useState<string>(BUTTON_LABELS.sendLink)
    const responseRegex = new RegExp(/"|\;/g)
    const parseResponse = (message: string) => String(message).replace(responseRegex, '')

    async function submitData(formData: RequestType) {
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
        const request = new URLSearchParams()

        Object.entries(formData).forEach(([key, value]) => {
            if (value) request.set(key, value.toString())
        })

        try {
            const response = await legacyWebService.post('/app/Sms.action', request, { headers })
            const { data } = response

            // If a phone number fails API validation, the response is a 200 with a user-friendly
            // error message in the body. If the message is anything but success, we throw an error.
            if (!data.includes(API_SUCCESS_RESPONSE)) {
                appLogger.logError(new Error(`Send download link failed due to: ${data}`))
                throw new Error(parseResponse(data))
            }

            setErrorMsg(null)
            setPhoneNumber('')
            setButtonLabel(BUTTON_LABELS.linkSent)
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : JSON.stringify(err)

            appLogger.logError(new Error(`Send download link failed due to: ${errorMessage}`))
            setErrorMsg(errorMessage)
        } finally {
            setIsLoading(false)
        }
    }

    const isPhoneNumberValid = (value: string) => {
        const regex = new RegExp(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/)
        return !!value && regex.test(value)
    }

    const handlePhoneNumberBlur = (event: FormEvent<HTMLInputElement>) => {
        const { value = '' } = event.currentTarget
        const errorMessage = isPhoneNumberValid(value) ? null : ERROR_MESSAGE

        setErrorMsg(errorMessage)
        setPhoneNumber(value)
    }

    const handlePhoneNumberChange = (event: FormEvent<HTMLInputElement>) => {
        const { value = '' } = event.currentTarget
        if (buttonLabel !== BUTTON_LABELS.sendLink) setButtonLabel(BUTTON_LABELS.sendLink)

        setErrorMsg(null)
        setPhoneNumber(value)
    }

    const handleSubmit = useCallback(() => {
        const isValid = isPhoneNumberValid(phoneNumber)
        if (!isValid) return setErrorMsg(ERROR_MESSAGE)

        const request: RequestType = {
            phoneNumber,
            isReferral: true,
            referralDownloadUrlString: REFERRAL_DOWNLOAD_URL_STRING,
        }

        setIsLoading(true)
        submitData(request)
    }, [phoneNumber])

    const isButtonDisabled = !!errorMsg || isLoading || !isPhoneNumberValid(phoneNumber)

    return (
        <Box className={styles.formContainer} marginBottom={8}>
            <Text className={styles.desktopInstructions} size="md">
                Enter your phone number, or scan the QR code to enroll and earn.
            </Text>
            <InputMask mask="999-999-9999" value={phoneNumber} maskChar={null} onChange={handlePhoneNumberChange}>
                {(inputProps: InputState) => (
                    <Input
                        {...inputProps}
                        placeholder="(555) 555-5555"
                        variant="block"
                        type="tel"
                        required
                        pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                        onBlurCapture={handlePhoneNumberBlur}
                        data-testid="send-download-link-input"
                        className={styles.input}
                        label="Phone number"
                        maxLength={12}
                    />
                )}
            </InputMask>
            {errorMsg && (
                <Box display="flex" align="center" marginTop={3}>
                    <Icon type="close-circle" className={styles.errorIcon} />
                    <Text className={styles.errorText} altFont size="md" data-testid="send-download-link-error">
                        {errorMsg}
                    </Text>
                </Box>
            )}
            <Button
                className={styles.sendButton}
                onClick={handleSubmit}
                disabled={isButtonDisabled}
                data-testid="send-download-link-button"
            >
                {buttonLabel}
            </Button>
            <Text size="sm" className={styles.desktopAvailable}>
                Available in both the App store & the Google&nbsp;Play&nbsp;store
            </Text>
        </Box>
    )
}

export default SendDownloadLink
